import { useState, useEffect, useMemo, useCallback } from 'react';
import { ICONS, Icon } from 'src/modules/ui-elements/Icon';
import PrimaryButton, { ButtonTypes } from "src/modules/ui-elements/PrimaryButton";
import { css } from "@emotion/react"
import AccountPopover from 'src/layouts/dashboard/header/AccountPopover';
import { useRouter } from 'next/router';
import useAuth from '@/hooks/useAuth';
import { Badge, CircularProgress } from '@mui/material';
import Subscription from '@/modules/Subscription';
import { useRef } from 'react';
import CustomizedButton from '@/modules/ui-elements/CustomizedButton';
import { TrackWebappEvent } from '@/modules/segment-event-tracking/segmentEventTracking';
import { EventTrackName } from '@/modules/segment-event-tracking/constant';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { activeProjectAtom, historyDalleAtom, pageStatesAtom, pagesAtom, segmentEventPayload, settingProjectNameAtom, showLoginPopupAtom, standardInfographic, textureAndColorActiveAtom } from '../Atoms';
import { debounce, isNumber, set } from 'lodash';
import { useSelector } from 'react-redux';
import ErrorBoundary from "@/modules/canvas/errorBoundry";
import useEditorService from '@/hooks/useEditorService';
import GetAppLinkByScan from "src/modules/home/GetAppLinkByScan";
import PrimaryInput from '@/modules/ui-elements/PrimaryInput';
import { setJobName } from '../services/Jobs';
import PublishPopUp from '../PublishPopUp';
import ConfirmJobPopUpV2 from '../ConfirmJobPopUpV2';
import ResizePopUp from '../ResizePopUp';
import { InfographicsSelector } from '../InforgraphicsHeader/InfographicSelector';
import { usePathname } from 'next/navigation';
import useToast from "@/hooks/useToast";


const convertToCSV = async (pageStates, pages, jobOrder_number, projectName) => {
    let csv = 'Project_ID,parentJobId,id,type,image_url,bg_color / solid fill,text,text_color,font_size,font_weight,font_family,border_color,border_thickness\n';
    
      await Promise.all(pageStates?.map(async (x, i) => {
        const editor = pages[i];
        let data = await editor.save();
        const projectID = projectName || '';
        const parentJobId = jobOrder_number || '';

        
        data?.layers?.forEach(layer => {
            const id = layer?.id || '';
            const imageUrl = layer?.src || '';
            const text = layer?.text || '';
            const textColor = layer?.textColor || '';
            const fontSize = layer?.fontSize || '';
            const fontWeight = layer?.fontWeight || '';
            const fontFamily = layer?.fontFamily || '';
            const borderColor = layer?.borderColor || '';
            const borderThickness = layer?.borderThickness || '';
            const solidFill = layer?.fill?.backgroundColor || '';
            const fillBackgroundUrl = layer?.fill?.backgroundImageSrc || '';
    
            if (layer.type === 'image') {
                csv += `${projectID},${parentJobId},${id},${layer.type},${imageUrl},,,,,,,,,\n`;
            } else if (layer.type === 'text') {
                csv += `${projectID},${parentJobId},${id},${layer.type},,,"${text}",${textColor},${fontSize},${fontWeight},${fontFamily},,\n`;
            } else if (layer.type === 'svg') {
                csv += `${projectID},${parentJobId},${id},${layer.type},${fillBackgroundUrl},${solidFill},,,,,,${borderColor},${borderThickness}\n`;
            }
        });
    
        let bgColor = data.backgroundInfo ? ( data.backgroundInfo?.src ? '' :  data.backgroundInfo?.color || '' ): '';
        let imageUrl = data.backgroundInfo?.src || '';
        csv += `${projectID},${parentJobId},background,background,${imageUrl},${bgColor},${bgColor ? 'Solid color' : ''},,,,,,,\n`;
     }));
    
     return csv;
};

export const HeaderType = {
    DARK: 'DARK',
    LIGHT: 'LIGHT'
};

export default function Header({ type = HeaderType.DARK, studio = false }) {
    const pathname = usePathname();
    const regex = /dashboard\/editor/;
    const isEditor = regex.test(pathname);
    const toaster = useToast();
    const projectName = useAtomValue(activeProjectAtom)?.name;
    const activeJobInfo = useAtomValue(activeProjectAtom);
    const jobIdRef=useRef(null);
    const job_id = useMemo(()=>{
        jobIdRef.current = activeJobInfo?.job_id;
        return activeJobInfo?.job_id
    },[activeJobInfo]);
    const oldProjectNameRef = useRef(projectName); 


    const { user, isAuthenticated } = useAuth();
    const popUpRef = useRef(null);
    const typeBasedStyles = getCorrespondingStyleObject(type);
    const { push, back } = useRouter();
    const { destroy } = useEditorService();
    const setLoginPopupAtom = useSetAtom(showLoginPopupAtom);
    const getSegmentPayloadAtom = useAtomValue(segmentEventPayload);
    const { entireStrapiData } = useSelector((state) => state.strapi);
    const strapiData = entireStrapiData?.StudioHeaderText?.StudioHeaderText;
    const creditScreenStrapiData = entireStrapiData?.CreditScreen;
    const webStrapiData = entireStrapiData?.WebappHomepageV22?.WebappHomepageV22;
    const [notificationValue, setNotification] = useState();
    
    const getNotification = async () => {
        const data = await API_SERVICE.get(`${process.env.NEXT_PUBLIC_API_URL_V2}v1/api/chat/getRecentTicket/0/25/?userId=${get( user,["user", "_id"],  "" )}`);
        const totalUserCount = data.data?.data?.reduce( (sum, item) => sum + +item.unreadChatCount, 0);
        const genericData = await API_SERVICE.get( `${process.env.NEXT_PUBLIC_API_URL_V2}v1/api/chat/getExternalChatUnreadCount`);
        const genericChatCount = genericData.data?.data?.genericChat;
        setNotification(totalUserCount + genericChatCount);
    }
    const infographicStrapiData = entireStrapiData?.InfographicsPanel?.InfographicsPanel;
    const [pageStates, setPageSates]= useAtom(pageStatesAtom);
    const [pages, setPages] = useAtom(pagesAtom);

    const isInfoGraphicUser = user?.user?.infoGraphicsUser || false;
    const [activeProject, setActiveProject] = useAtom(activeProjectAtom);
    const setProjectName = (v) => {
        setActiveProject((prev) => ({ ...prev, name: v }));
    }

    const [isProjectPaneOpen, setIsProjectPaneOpen] = useState(false);
    const toggleIsProjectPaneOpen = () => setIsProjectPaneOpen(x => !x);

    const [projectFilterInfographics, setProjectFilterInfographics] = useState('all');
    const handleProjectFilterChange = (newFilter) => {
        setProjectFilterInfographics(newFilter);
    };

    const [hasJobs, setJobs] = useState(false);
    const handleJobCheck = (job) => {
        setJobs(job);
    }

    const [isNewProjectClicked, setIsNewProjectClicked] = useState(false);
  const setHistoryValue = useSetAtom(historyDalleAtom);
    const onNewProjectClick = (e) => {
        if (!buttonRef.current) return;
        setIsNewProjectClicked(!isNewProjectClicked);
        const boundingRect = buttonRef.current.getBoundingClientRect();
        const top = boundingRect.bottom;
        const left = boundingRect.left;
        const height = window.innerHeight - boundingRect.bottom - 4;
        const width = boundingRect.width;
        resizePopUpRef.current.open({ height, width, left, top });
        setHistoryValue(false);
        if (Object.keys(getSegmentPayloadAtom).length) TrackWebappEvent(`${process.env.NEXT_PUBLIC_EVENT_TRACK_TYPE}${EventTrackName._click_new_proj_projects_studio}`, getSegmentPayloadAtom);
    }
    const [settingProjectName, setSettingProjectName] = useAtom(settingProjectNameAtom);

    const buttonRef = useRef(null);
    const isHidden = useMemo(() => !isProjectPaneOpen && activeProject, [isProjectPaneOpen, activeProject]);

    let count = useRef(0);
    const onProjectNameChange = async (e) => {
        const regex = /^[-_a-zA-Z0-9 ]*$/;
        const regex2 = /^ *$/;
    
        const customRegex = user?.entity?.entity?.jobLabel_Regex
          ? new RegExp(user.entity.entity.jobLabel_Regex)
          : null
          
        if (!customRegex) {
          if (
            e.target.value.length > 0 &&
            !regex.test(e.target.value[e.target.value.length - 1])
          )
            e.target.value = e.target.value.slice(0, -1);
          if (!regex.test(e.target.value))
            e.target.value = e.target.value.replace(/[^-_a-zA-Z0-9 ]/g, "-");
          if (regex2.test(e.target.value)) return;
           debouncedProjectNameApiCall(e.target.value, count.current)
        }else{
             validateCustomRegex(e.target.value)
        }
        setProjectName(e.target.value);
      };
      const validateCustomRegex = useCallback(
        debounce(async (value) => {
            const customRegex = user?.entity?.entity?.jobLabel_Regex
                ? new RegExp(user.entity.entity.jobLabel_Regex)
                : null;
            if (customRegex) {
                if (!customRegex.test(value)) {
                    console.log(oldProjectNameRef.current, "Invalid Name");
                    setProjectName(oldProjectNameRef.current); 
                    toaster.error("Invalid Name");
                } else {
                    oldProjectNameRef.current = value; 
                    setProjectName(value);
                    await debouncedProjectNameApiCall(value, count.current);
                }
            }
        }, 3000, { trailing: true, leading: false }),
        [user?.entity?.entity?.jobLabel_Regex, user?.entity?.entity?.jobLabel_Regex_Flag]
    );

    const debouncedProjectNameApiCall = useCallback(debounce(async (projectName, currCount) => {
        count.current++;
        setSettingProjectName(true);
        // console.log(jobIdRef.current,"job_idjob_idjob_id");
        await setJobName(jobIdRef.current, projectName).catch(console.error);
        if (count.current == currCount + 1) setSettingProjectName(false);
    }, 200, { trailing: true, leading: false }), [job_id]);

    const onGoBatchEditor = () => {
        destroy();
        push('/dashboard/batch-editor');
        if (Object.keys(getSegmentPayloadAtom).length) TrackWebappEvent(`${process.env.NEXT_PUBLIC_EVENT_TRACK_TYPE}${EventTrackName._click_batch_editor_cta}`, getSegmentPayloadAtom);
    };

    const onGoStudio = () => {
        destroy();
        push('/dashboard/editor');
    };

    const onGoUpgradePlan = () => {
        if (user?.user?.login_type === "Guest") {
            popUpRef.current?.open();
        } else {
            destroy();
            push('/dashboard/setting?tab=PAY_PER_USE');
        }
    };

    const onLoginHandler = () => {
        setLoginPopupAtom(true);
        if (Object.keys(getSegmentPayloadAtom).length) TrackWebappEvent(`${process.env.NEXT_PUBLIC_EVENT_TRACK_TYPE}${EventTrackName._click_login_hp}`, getSegmentPayloadAtom);
    };

    const onGoDemoPage = () => {
        window.open('https://www.domyshoot.com/bookAdemo?source=website', "_blank")
    };

    const [openScanAppQrDialog, setOpenScanAppQrDialog] = useState(false);
    const [hoveredApp, setHoveredApp] = useState(false);
    const [hoveredDemo, setHoveredDemo] = useState(false);
    const [hoveredBatch, setHoveredBatch] = useState(false);

    const isTextureAndColorActive = useAtomValue(textureAndColorActiveAtom);
    const publishPopUpref = useRef(null);
    const exportHandler = async () => {
        if (isTextureAndColorActive) return toaster.info("Please close the 'Texture and Color' feature to continue with the download.", 2000);
        if (isInfoGraphicUser) {
            publishPopUpref.current.open()
        }
        else {
            popUpRef.current.open();
        }
        if (Object.keys(getSegmentPayloadAtom).length) TrackWebappEvent(`${process.env.NEXT_PUBLIC_EVENT_TRACK_TYPE}${EventTrackName._export_studio}`, { ...getSegmentPayloadAtom, job_id: activeProject?.job_id, job_name: projectName });
    };

    const resizePopUpRef = useRef(null);

    const isStandardInfographic = useAtomValue(standardInfographic);
    const downloadCSV = (csvContent,filename) => {
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const exportCSVHandler = async () => {
        try {
            const csvData = await convertToCSV(pageStates, pages, activeProject?.jobOrder_number, projectName);
            downloadCSV(csvData, projectName);
        } catch (error) {
            console.error(error);
        }
    };


    return (
        <div css={[typeBasedStyles.containerCss, typeBasedStyles.container]}>
            <div style={styles.innerContainer}>
                <div style={styles.leftSection}>
                    <div style={styles.logoContainer} onClick={()=>{
                        destroy();
                        push("/dashboard/homePage")
                    }}>
                        {type === HeaderType.DARK 
                            ? 
                                <img src={webStrapiData?.WebAppDresmaDarkIconLogo || ICONS.whiteLogo} width="42" height="42" /> 
                            :
                                <img src={webStrapiData?.WebAppDresmaIconLogo || ICONS.onlyLogo} width="42" height="42"/>
                        }
                        <div css={typeBasedStyles.leftSectionDivider}></div>  
                    </div>
                    
                    <ErrorBoundary name={"Infographic Selector"}>{isInfoGraphicUser && window.location.pathname.includes('/dashboard/editor') && <InfographicsSelector />}</ErrorBoundary>

                    {
                        isEditor && activeProject && isHidden ? <div style={styles.button2}>
                            <PrimaryInput type={"darkNoBorders"} size={"xl"} value={projectName || ""} disabled={!activeProject} onChange={onProjectNameChange} loading={settingProjectName} containerStyle={{ width: "353px", borderRadius: "4px", padding: "7px 10px", display: "flex", flexDirection: "row-reverse", border: `${isInfoGraphicUser && "1px solid #EEEEEE66"}` }} icon={!isInfoGraphicUser && ICONS.editTwo} />
                        </div> : <></>
                    }
                    <div style={styles.leftSection}>
                        {!studio ? 
                        <PrimaryButton type={ButtonTypes.pinkBgPinkText} text={strapiData?.StudioText || "Studio"} icon={ICONS.pinkHome} style={{ ...styles.mr16, ...styles.ml32, ...styles.cursorPointer, ...styles.userSelectNone }} onClick={onGoStudio} /> :
                            <>
                            </>}

                        {!isEditor && <CustomizedButton type={type === HeaderType.LIGHT ? 'white_with_light_border' : 'black_with_white_border'} btnText={strapiData?.BookADemoCTA || `Book a Demo`} endIcon={type === HeaderType.LIGHT ? (hoveredDemo ? ICONS.demoLight : ICONS.demoDark) : (hoveredDemo ? ICONS.demoDark : ICONS.demoLight) } btnStyle={{...styles.mr24,...styles.cursorPointer,...styles.px6, ...styles.userSelectNone}} onClick={onGoDemoPage} onMouseEnter={()=>setHoveredDemo(true)} onMouseLeave={()=>setHoveredDemo(false)}/>}

                    </div>
                </div>
                <div style={{ ...styles.dFlex, ...{ alignItems: 'center', paddingRight: 18 } }}>
                    {/* Disabling Batch Editor point from My Project section DRES-9923 */}
                    {/* {!isEditor && <CustomizedButton type={type === HeaderType.LIGHT ? 'white_with_light_border' : 'black_with_white_border'} btnText={strapiData?.BatchPhotoEditor || `Batch Editor`} startIcon={type === HeaderType.LIGHT ? (hoveredBatch ? ICONS.batchImage : ICONS.batchBlack) : (hoveredBatch ? ICONS.batchBlack : ICONS.batchImage)} btnStyle={{...styles.mr24,...styles.cursorPointer,...styles.px6}} onClick={onGoBatchEditor} onMouseEnter={()=>setHoveredBatch(true)} onMouseLeave={()=>setHoveredBatch(false)}/>} */}
                    {!isEditor && <CustomizedButton type={type === HeaderType.LIGHT ? 'white_with_border' : 'black_with_white_border'} btnText={`${strapiData?.getApp || "Get the App" }`} startIcon={hoveredApp ? ICONS.mobileDark : ICONS.mobile} btnStyle={{...styles.mr24,...styles.cursorPointer,...styles.px6, ...styles.userSelectNone}} onClick={() => setOpenScanAppQrDialog(true)} onMouseEnter={()=>setHoveredApp(true)} onMouseLeave={()=>setHoveredApp(false)}/>}

                    {isNumber(user?.entity?.entity?.credits) && user?.user?.login_type !== "Guest" && <PrimaryButton icon={ICONS.credits} type={type === HeaderType.LIGHT ? ButtonTypes.darkBgWhiteText : ButtonTypes.darkBgWhiteText} text={`${user?.entity?.entity?.credits} ${ user?.entity?.entity?.credits === 1 ? (creditScreenStrapiData?.OnlyCreditText || 'Credit') : (creditScreenStrapiData?.CreditHeading || "Credits")}`} style={{ ...styles.creditsBtnmr10, ...styles.userSelectNone }} onClick={onGoUpgradePlan} />}

                    {activeProject && <>
                        <div css={styles.rightSectionDivider}></div>
                        {isInfoGraphicUser && <CustomizedButton btnStyle={{ ...styles.userSelectNone }} type={"darkGrey"} startIcon={ICONS.downloadCsv} btnText={infographicStrapiData?.ExportCSVCTA || "export csv"} onClick={exportCSVHandler} />}
                        {/* <CustomizedButton type={"white_with_light_border"} startIcon={"/icons/publishDark.svg"} btnText={isInfoGraphicUser ? (isStandardInfographic ? "Publish for All" : "Publish") : "Download"} onClick={exportHandler} btnStyle={{ padding: "4px 10px", margin: "0 15px", ...styles.userSelectNone }} /> */}
                        <PublishPopUp ref={publishPopUpref} />
                        <ConfirmJobPopUpV2 ref={popUpRef} />
                    </>}

                    {user?.user?._id ? (isAuthenticated && user?.user?.login_type !== "Guest") ?
                        <AccountPopover /> :
                        <CustomizedButton type="pink_with_border" btnText={`${strapiData?.getfreepremium || "Log in"}`} btnStyle={{ ...styles.loginButton, ...styles.userSelectNone }} disabled={false} isActive={true} onClick={onLoginHandler} />
                        : <CustomizedButton type="pink_with_border" btnText={<CircularProgress size='17px' thickness={5} color='inherit' />} btnStyle={{ ...styles.loadingButton, ...styles.userSelectNone }} disabled={false} isActive={true} />
                    }
                </div>
                <Subscription popUpRef={popUpRef} />
                <GetAppLinkByScan open={openScanAppQrDialog} onClose={() => setOpenScanAppQrDialog(false)} />
                <ResizePopUp ref={resizePopUpRef} resizeAction="create-page" />
            </div>
        </div>
    )
}

const LogoImageLightBackground = () => <svg xmlns="http://www.w3.org/2000/svg" width="138" height="39" viewBox="0 0 138 39" fill="none" style={{ marginTop: 6, marginBottom: 6 }}>
    <g clip-path="url(#clip0_353_33591)">
        <path d="M32.6278 5.40386V33.3025C32.6228 34.5864 32.1339 35.8162 31.2675 36.7236C30.401 37.631 29.2276 38.1424 28.003 38.1463H18.416V31.0034C19.253 30.8264 20.0709 30.5614 20.8573 30.2125V36.0094H27.6469C29.1697 36.0094 30.1865 34.3587 30.1865 32.7573V4.84874C30.1854 4.23202 30.0407 3.62492 29.7648 3.0802C29.5558 2.79316 29.2873 2.5596 28.9797 2.39707C28.6721 2.23455 28.3334 2.14729 27.989 2.14189H20.8431V8.35142C20.0623 8.01007 19.2485 7.75808 18.416 7.59979V0H27.6469C29.6149 0 30.9269 0.884268 31.6578 2.21067C32.3154 3.12993 32.6572 4.25524 32.6278 5.40386Z" fill="black" />
        <path d="M17.9885 5.98903C17.1461 5.6559 16.273 5.41553 15.3833 5.27179C14.6983 5.15717 14.0058 5.09967 13.3122 5.09985C9.78155 5.09985 6.39556 6.57028 3.89904 9.18767C1.40253 11.805 0 15.355 0 19.0566C0 22.7581 1.40253 26.3081 3.89904 28.9255C6.39556 31.5429 9.78155 33.0133 13.3122 33.0133C14.0057 33.0151 14.6982 32.9592 15.3833 32.8462C16.273 32.7025 17.1461 32.4621 17.9885 32.129C20.5251 31.1301 22.7106 29.3436 24.2533 27.0083C25.7961 24.6729 26.6227 21.8998 26.6227 19.059C26.6227 16.2182 25.7961 13.445 24.2533 11.1097C22.7106 8.77433 20.5251 6.98795 17.9885 5.98903ZM17.9885 29.1766C17.1621 29.5999 16.2862 29.9071 15.3833 30.0903C13.914 30.3967 12.3997 30.377 10.9381 30.0326C9.47648 29.6882 8.0999 29.0264 6.89706 28.0902C5.69422 27.154 4.69177 25.964 3.95434 24.5968C3.21689 23.2297 2.76081 21.7157 2.61545 20.1525C2.4701 18.5893 2.63871 17.0115 3.11041 15.5207C3.58211 14.03 4.34645 12.6594 5.35419 11.4972C6.36191 10.3351 7.59068 9.40713 8.96132 8.77318C10.332 8.13923 11.814 7.81335 13.3122 7.81652C14.0076 7.81428 14.7014 7.88504 15.3833 8.02777C17.5888 8.48426 19.6019 9.65585 21.1379 11.377C22.674 13.0981 23.6553 15.2819 23.9432 17.6194C24.2312 19.957 23.8113 22.3305 22.7427 24.4043C21.6742 26.4782 20.0111 28.1475 17.9885 29.1766Z" fill="black" />
        <path d="M13.3157 11.2354C11.839 11.2343 10.3952 11.6927 9.167 12.5522C7.93876 13.4117 6.98131 14.6339 6.41577 16.064C5.85023 17.4942 5.70202 19.0681 5.98988 20.5866C6.27775 22.1052 6.98875 23.5 8.03295 24.5948C9.07714 25.6895 10.4076 26.435 11.8559 26.7368C13.3044 27.0386 14.8055 26.8832 16.1697 26.2903C17.5338 25.6973 18.6996 24.6935 19.5194 23.4058C20.3392 22.1181 20.7763 20.6044 20.7754 19.0561C20.7754 16.982 19.9895 14.9927 18.5905 13.526C17.1916 12.0593 15.2941 11.2354 13.3157 11.2354ZM13.3157 24.1014C12.3639 24.1014 11.4336 23.8056 10.6422 23.2511C9.85083 22.6968 9.23403 21.9088 8.8698 20.9869C8.50557 20.065 8.41027 19.0506 8.59596 18.072C8.78164 17.0933 9.23996 16.1943 9.91297 15.4887C10.586 14.7831 11.4434 14.3025 12.377 14.1079C13.3104 13.9132 14.278 14.0131 15.1573 14.395C16.0366 14.7769 16.7882 15.4235 17.317 16.2532C17.8458 17.0829 18.128 18.0584 18.128 19.0561C18.128 19.7188 18.0035 20.3748 17.7616 20.9869C17.5199 21.599 17.1653 22.1552 16.7185 22.6237C16.2717 23.0922 15.7411 23.4638 15.1573 23.7174C14.5735 23.971 13.9477 24.1014 13.3157 24.1014Z" fill="#FB2B3A" />
        <path d="M47.7744 12.7983H42.792V25.9512H47.7744C51.2333 25.9512 53.814 23.0764 53.814 19.3748C53.814 15.6732 51.2333 12.7983 47.7744 12.7983ZM47.7744 23.471H45.2652V15.2786H47.7744C49.9429 15.2786 51.4484 16.9509 51.4484 19.3748C51.4484 21.7986 49.9429 23.471 47.7744 23.471Z" fill="black" />
        <path d="M59.8774 26.214C62.5119 26.214 64.6267 24.0532 64.6267 21.2536C64.6267 18.4538 62.5119 16.293 59.8774 16.293C57.2428 16.293 55.146 18.4538 55.146 21.2536C55.146 24.0532 57.2428 26.214 59.8774 26.214ZM59.8774 23.8465C58.5153 23.8465 57.4579 22.7755 57.4579 21.2536C57.4579 19.7315 58.5153 18.6605 59.8774 18.6605C61.2574 18.6605 62.3148 19.7315 62.3148 21.2536C62.3148 22.7755 61.2574 23.8465 59.8774 23.8465Z" fill="black" />
        <path d="M78.7878 12.7983H76.2429L72.6585 19.0178L69.0562 12.7983H66.5112V25.9512H68.9845V17.4394L72.5151 23.5273H72.8019L76.3326 17.4207V25.9512H78.7878V12.7983Z" fill="black" />
        <path d="M87.0113 16.5564L85.0219 22.8697L82.692 16.5564H80.165L83.8391 25.6507C83.4089 26.9095 82.7637 27.4732 81.4913 27.4545V29.7093C83.7674 29.8407 85.2907 28.657 86.2227 25.9513L89.4845 16.5564H87.0113Z" fill="black" />
        <path d="M94.7768 26.2143C97.4471 26.2143 99.329 24.7112 99.329 22.2497C99.329 19.5627 97.2141 18.8675 95.2248 18.2099C93.3251 17.6086 92.8412 17.1765 92.8412 16.3684C92.8412 15.6168 93.4505 15.0532 94.49 15.0532C95.7624 15.0532 96.4434 15.7484 96.8915 16.7443L98.9705 15.4853C98.1281 13.644 96.551 12.5354 94.49 12.5354C92.3393 12.5354 90.3679 13.9634 90.3679 16.4436C90.3679 18.9426 92.2318 19.7883 94.1495 20.3707C96.0134 20.9344 96.8556 21.3102 96.8556 22.2873C96.8556 23.02 96.3359 23.6777 94.8485 23.6777C93.2892 23.6777 92.411 22.8885 91.963 21.6672L89.8481 22.9637C90.5292 24.9178 92.2139 26.2143 94.7768 26.2143Z" fill="black" />
        <path d="M105.968 16.2932C104.767 16.2932 103.835 16.763 103.297 17.6086V12.7983H100.985V25.9512H103.297V20.878C103.297 19.2433 104.14 18.548 105.269 18.548C106.308 18.548 107.043 19.2057 107.043 20.4834V25.9512H109.355V20.1828C109.355 17.6838 107.867 16.2932 105.968 16.2932Z" fill="black" />
        <path d="M115.745 26.214C118.38 26.214 120.495 24.0532 120.495 21.2536C120.495 18.4538 118.38 16.293 115.745 16.293C113.111 16.293 111.014 18.4538 111.014 21.2536C111.014 24.0532 113.111 26.214 115.745 26.214ZM115.745 23.8465C114.383 23.8465 113.326 22.7755 113.326 21.2536C113.326 19.7315 114.383 18.6605 115.745 18.6605C117.125 18.6605 118.183 19.7315 118.183 21.2536C118.183 22.7755 117.125 23.8465 115.745 23.8465Z" fill="black" />
        <path d="M126.538 26.214C129.172 26.214 131.287 24.0532 131.287 21.2536C131.287 18.4538 129.172 16.293 126.538 16.293C123.903 16.293 121.806 18.4538 121.806 21.2536C121.806 24.0532 123.903 26.214 126.538 26.214ZM126.538 23.8465C125.176 23.8465 124.118 22.7755 124.118 21.2536C124.118 19.7315 125.176 18.6605 126.538 18.6605C127.918 18.6605 128.975 19.7315 128.975 21.2536C128.975 22.7755 127.918 23.8465 126.538 23.8465Z" fill="black" />
        <path d="M137.998 18.8863V16.5563H135.973V13.9258L133.661 14.6585V16.5563H132.101V18.8863H133.661V22.7945C133.661 25.3311 134.753 26.327 137.998 25.9512V23.7528C136.672 23.828 135.973 23.8092 135.973 22.7945V18.8863H137.998Z" fill="black" />
    </g>
    <defs>
        <clipPath id="clip0_353_33591">
            <rect width="138" height="38.1463" fill="white" />
        </clipPath>
    </defs>
</svg>

const styles = {
    containerCss: css`
    box-shadow: 0px 3px 32px rgba(38, 38, 38, 0.08);
    border-bottom: 1px solid rgba(255, 255, 255, 0.16);
    `,
    container: {background:'#121212',  flexDirection:'row', display:"flex"},
    innerContainer: {display:'flex',flex:1, flexDirection:"row", flex:1, justifyContent:"space-between", alignItems:'center'},
    leftSection: {display:'flex', flexDirection:'row', alignItems:'center'},
    logoContainer: {paddingTop:"10px", paddingLeft:"16px", paddingRight:"16px", paddingBottom:"10px", display:'flex', flexDirection:'row', cursor:'pointer'},
    leftSectionDivider: {width:"1px", marginTop:-24, marginBottom:-24, marginLeft:"32px", marginRight:"-16px", backgroundColor:'rgba(255, 255, 255, 0.08)'},
    mr16: {marginRight:16},
    dFlex: { display: "flex" },
    mr24: { marginRight: 24 },
    px6: { paddingTop: '6px', paddingBottom: '6px' },
    profile: { background: "#FFFFFFA0", height: 32, width: 32, border: "0px solid transparent", borderRadius: "16px", marginRight: "16px" },
    mr10: { marginRight: "10px", background: "rgba(95, 46, 229, 1)", color: "white" },
    creditsBtnmr10: { marginRight: "10px", background: "rgba(95, 46, 229, 1)", color: "white", background: "#333333", borderRadius: "4px", padding: "4px 15px" },
    ml32: { marginLeft: "32px" },
    cursorPointer: { cursor: 'pointer' },
    userSelectNone: { userSelect: 'none' },
    loginButton: {
        padding: '6px 24px',
    },
    loadingButton: {
        padding: '4px 38px'
    },
    notificationBox: css`
        position:relative;
        width:32px;
        height:32px;
        margin-right:24px;
    `,
    badge: {
        position: "absolute",
        top: 3,
        right: 0
    },
    endIconStyle: (isNewProjectClicked) => ({
        transform: `rotate(${isNewProjectClicked ? 180 : 0}deg)`,
    }),
    btnTxtStyle: (isInfoGraphicUser) => ({
        minWidth: isInfoGraphicUser ? 90 : 250,
        width: isInfoGraphicUser ? '50%' : '100%'
    }),
}

function getCorrespondingStyleObject(type) {
    let containerCss, container, leftSectionDivider, profile;

    switch (type) {
        case HeaderType.DARK: {
            containerCss = css`
            box-shadow: 0px 3px 32px rgba(38, 38, 38, 0.08);
            border-bottom: 1px solid rgba(255, 255, 255, 0.16);
            `;
            container = css`
            background:#121212;
            flexDirection:row;
            display:flex;
            `;
            leftSectionDivider = css`
            width:1px;
            margin-top:-24;
            margin-bottom:-24;
            margin-left:16px;
            margin-right:-16px;
            background-color:rgba(255, 255, 255, 0.08);
            `;
            profile = css`
            background:#FFFFFFA0;
            height:32;
            width:32;
            border:0px solid transparent;
            border-radius:16px;
            margin-right:16px;
            `;
            break;
        }
        case HeaderType.LIGHT: {
            containerCss = css`
            position:fixed;
            top:0;
            left:0;
            width:100vw;
            z-index:11;
            box-shadow: 0px 3px 32px rgba(38, 38, 38, 0.08);
            border-bottom:1px solid #EEE;;
            `;
            container = css`
            background:rgba(255, 255, 255, 1);
            flexDirection:row;
            display:flex;
            `;
            leftSectionDivider = css`
            width:1px;
            margin-top:-24;
            margin-bottom:-24;
            margin-left:32px;
            margin-right:-16px;
            background-color:rgba(255, 255, 255, 1);
            `;
            profile = css`
            background:#FFFFFFA0;
            height:32;
            width:32;
            border:0px solid transparent;
            border-radius:16px;
            margin-right:16px;
            `;
        }
    }

    return { containerCss, container, leftSectionDivider, profile }
}